import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines, EASE } from "./Reveal";
import { COMPANY } from "../../data/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1631510390389-c1e4fb20ff31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGJyaWdodCUyMG1pbmltYWxpc3QlMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc4NjY5OTg3MHww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStart(true), 250);
    return () => clearTimeout(t);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen pt-28 pb-16 overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: start ? 1 : 0 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-[#7B8A83] mb-8"
        >
          Residential cleaning & home assistance · {COMPANY.area}
        </motion.p>

        <h1 className="font-serif font-light tracking-tight leading-[0.98] text-[#1A362D] text-[15vw] sm:text-7xl md:text-8xl lg:text-[7.5rem]">
          <MaskLines start={start} lines={["More time for the", "things that "]} delay={0.25} />
          <MaskLines start={start} lines={["matter most."]} delay={0.5} className="italic text-[#C26D5C]" />
        </h1>

        <div className="mt-14 grid md:grid-cols-12 gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: start ? 1 : 0, y: start ? 0 : 30 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
            className="md:col-span-5"
          >
            <p className="text-lg md:text-xl text-[#4A5A53] leading-relaxed max-w-md">
              Trusted home assistants for busy individuals and families — cleaning, meal prep,
              laundry, errands and more. One simple hourly rate.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-book-button"
                onClick={() => scrollTo("#book")}
                className="group inline-flex items-center gap-2 rounded-full bg-[#1A362D] text-[#F9F8F6] px-7 py-4 text-sm font-medium hover:bg-[#C26D5C] transition-colors duration-300"
              >
                Request a free quote
                <ArrowDownRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
              </button>
              <button
                data-testid="hero-services-button"
                onClick={() => scrollTo("#services")}
                className="rounded-full border border-[#1A362D] text-[#1A362D] px-7 py-4 text-sm font-medium hover:bg-[#1A362D] hover:text-[#F9F8F6] transition-colors duration-300"
              >
                Explore services
              </button>
            </div>
          </motion.div>

          <div className="md:col-span-7">
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: start ? "inset(0% 0% 0% 0%)" : "inset(100% 0% 0% 0%)" }}
              transition={{ duration: 1.2, ease: EASE, delay: 0.55 }}
              className="relative rounded-[2rem] overflow-hidden aspect-[16/11] border border-[#D5D1C8]"
            >
              <motion.img
                src={HERO_IMG}
                alt="Bright, calm living room"
                style={{ y: imgY, scale: imgScale }}
                className="w-full h-[120%] object-cover"
              />
              <div className="absolute bottom-5 left-5 bg-[#F9F8F6]/90 backdrop-blur-md rounded-2xl px-5 py-4 border border-[#D5D1C8]">
                <p className="font-serif text-3xl text-[#1A362D] leading-none">2hr</p>
                <p className="text-xs text-[#7B8A83] mt-1 tracking-wide uppercase">minimum booking</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
